import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"

type PreferredLanguage = "fr" | "en"

const fallbackLanguage: PreferredLanguage = "fr"

const isPreferredLanguage = (value: string | null | undefined): value is PreferredLanguage =>
	value === "fr" || value === "en"

export function useLanguagePreference() {
	const store = UseRootStore()
	const { t, locale } = useI18n()

	const languageDraft = ref<PreferredLanguage>(fallbackLanguage)
	const savedLanguage = ref<PreferredLanguage>(fallbackLanguage)

	const currentUserDetails = computed(() => store.CurrentUserBasicDetails ?? store.CurrentUserDetails)

	const languageOptions = computed<{ id: PreferredLanguage; label: string }[]>(() => [
		{ id: "fr", label: t("my-profile.language.options.fr") },
		{ id: "en", label: t("my-profile.language.options.en") },
	])

	const hasLanguagePendingChanges = computed(() => languageDraft.value !== savedLanguage.value)

	const syncLanguageFromProfile = () => {
		const profileLanguage = currentUserDetails.value?.preferredLanguage
		const localeLanguage = String(locale.value)
		const language = isPreferredLanguage(profileLanguage)
			? profileLanguage
			: isPreferredLanguage(localeLanguage)
				? localeLanguage
				: fallbackLanguage
		languageDraft.value = language
		savedLanguage.value = language
	}

	const saveLanguagePreference = async () => {
		if (!hasLanguagePendingChanges.value) return

		await store.savePreferredLanguage(languageDraft.value)
		savedLanguage.value = languageDraft.value
	}

	const resetLanguagePreference = () => {
		languageDraft.value = savedLanguage.value
	}

	return {
		languageDraft,
		savedLanguage,
		languageOptions,
		hasLanguagePendingChanges,
		syncLanguageFromProfile,
		saveLanguagePreference,
		resetLanguagePreference,
	}
}
